/**
 * Provider capability configuration for Self Repair import.
 *
 * Existing providers keep their current Import Report requirements
 * (username, password, and the combined Last 4 SSN / security-answer field).
 * CreditHeroScore is username/email + password only.
 */

export const CREDIT_HERO_SCORE_PROVIDER_ID = 'credit_hero_score';

const DEFAULT_MONITORING_CAPABILITIES = {
  requiresUsername: true,
  requiresPassword: true,
  requiresLastFourSsn: true,
  requiresSecurityQuestion: false,
  requiresSecurityAnswer: true,
  supportsHtmlReport: true,
  supportsJsonReport: false,
  reportType: 'three_bureau',
};

export const PROVIDER_CAPABILITIES = {
  identityiq: {
    id: 'identityiq',
    name: 'IdentityIQ',
    ...DEFAULT_MONITORING_CAPABILITIES,
  },
  smartcredit: {
    id: 'smartcredit',
    name: 'SmartCredit',
    ...DEFAULT_MONITORING_CAPABILITIES,
  },
  myscoreiq: {
    id: 'myscoreiq',
    name: 'MyScoreIQ',
    ...DEFAULT_MONITORING_CAPABILITIES,
  },
  myfreescorenow: {
    id: 'myfreescorenow',
    name: 'MyFreeScoreNow',
    ...DEFAULT_MONITORING_CAPABILITIES,
  },
  privacyguard: {
    id: 'privacyguard',
    name: 'PrivacyGuard',
    ...DEFAULT_MONITORING_CAPABILITIES,
  },
  [CREDIT_HERO_SCORE_PROVIDER_ID]: {
    id: CREDIT_HERO_SCORE_PROVIDER_ID,
    name: 'CreditHeroScore',
    requiresUsername: true,
    requiresPassword: true,
    requiresLastFourSsn: false,
    requiresSecurityQuestion: false,
    requiresSecurityAnswer: false,
    supportsHtmlReport: true,
    supportsJsonReport: true,
    reportType: 'three_bureau',
  },
};

export function normalizeCapabilityProviderId(providerId) {
  if (!providerId) return '';
  const compact = String(providerId).toLowerCase().trim().replace(/[\s-]+/g, '_');
  if (compact.replace(/_/g, '') === 'creditheroscore') {
    return CREDIT_HERO_SCORE_PROVIDER_ID;
  }
  return compact;
}

export function getProviderCapabilities(providerId) {
  const normalized = normalizeCapabilityProviderId(providerId);
  return PROVIDER_CAPABILITIES[normalized] || {
    id: normalized || 'unknown',
    name: providerId || 'Unknown',
    ...DEFAULT_MONITORING_CAPABILITIES,
  };
}

export function providerRequiresVerificationField(providerId) {
  const caps = getProviderCapabilities(providerId);
  return Boolean(caps.requiresLastFourSsn || caps.requiresSecurityAnswer || caps.requiresSecurityQuestion);
}

export function getSensitiveCredentialFieldsToClear(providerId) {
  const caps = getProviderCapabilities(providerId);
  const cleared = {};
  if (!caps.requiresLastFourSsn && !caps.requiresSecurityAnswer && !caps.requiresSecurityQuestion) {
    cleared.ssnOrSecurityAnswer = '';
    cleared.securityAnswer = '';
    cleared.ssn_last4 = '';
  }
  return cleared;
}
